import config from './config/server-config';
import { CREDENTIAL_FORM } from './constants/PathsConstant';
import { isSsoBasedOnUrl } from './utils/app-utils';
import { FE_ENV } from '../frontend-env';
import { clearTokenCache } from './utils/token-util';
import {
  tryAcquireCrossTabRefreshLock,
  releaseCrossTabRefreshLock,
  waitForTokenRefreshFromOtherTab,
} from './utils/auth-refresh-coordination';

/** Single-flight refresh so parallel 403s do not rotate the refresh token multiple times. */
let refreshInFlight = null;

let sessionExpiredLogoutInProgress = false;

/** Timer handle for the proactive pre-expiry refresh. */
let proactiveRefreshTimer = null;

export function cancelProactiveRefresh() {
  clearTimeout(proactiveRefreshTimer);
  proactiveRefreshTimer = null;
}

export function scheduleProactiveRefresh(token) {
  clearTimeout(proactiveRefreshTimer);
  proactiveRefreshTimer = null;
  if (!token || typeof window === 'undefined') return;
  try {
    const payloadB64 = token.split('.')[1];
    if (!payloadB64) return;
    const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof payload.exp !== 'number') return;
    const msUntilRefresh = payload.exp * 1000 - Date.now() - 5 * 60 * 1000;
    if (msUntilRefresh <= 0) {
      tryRefreshSession();
      return;
    }
    proactiveRefreshTimer = setTimeout(async () => {
      const ok = await tryRefreshSession();
      if (ok) {
        scheduleProactiveRefresh(localStorage.getItem('token'));
      }
    }, msUntilRefresh);
  } catch {
    // Malformed JWT — fall back to reactive refresh only.
  }
}

function performSessionExpiredLogout() {
  if (sessionExpiredLogoutInProgress || typeof window === 'undefined') {
    return;
  }
  sessionExpiredLogoutInProgress = true;

  clearTokenCache();

  const accessToken = window.localStorage.getItem('token');
  const cached = window.localStorage.getItem('userInfo');
  let userid;
  let sessionid;
  if (cached) {
    try {
      const u = JSON.parse(cached);
      userid = u.User_Id;
      sessionid = u.Session_Id;
    } catch {
      // ignore
    }
  }

  localStorage.removeItem('token');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('notificationData');
  localStorage.removeItem('alertNotificationData');
  localStorage.removeItem('investorFilterData');
  localStorage.removeItem('companyFilterData');
  localStorage.removeItem('userInfo');
  localStorage.removeItem('preferredCurrency');
  localStorage.removeItem('persist:root');

  const goLogin = () => {
    window.location.href = CREDENTIAL_FORM;
  };

  if (userid != null && sessionid != null) {
    fetch(config.logout, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userid, sessionid, accessToken }),
    }).finally(goLogin);
  } else {
    goLogin();
  }
}

function createBaseUrl() {
  if (
    FE_ENV.NODE_ENV === 'development' ||
    window.location.hostname === 'stageapp.insightia.com' ||
    window.location.hostname === 'one.insightia.com' ||
    window.location.hostname === 'localhost'
  ) {
    return '';
  }

  if (window.location.hostname.endsWith('.highbond-s3.com')) {
    const featureBranchMatch = window.location.hostname.match(/^([a-z0-9-]+)\.dmi-uk\.highbond-s3\.com$/);
    if (featureBranchMatch) {
      const branchName = featureBranchMatch[1];
      return `https://api-${branchName}.dmi-uk.highbond-s3.com`;
    }
    return 'https://api.dmi-uk.highbond-s3.com';
  }

  return `https://api.${window.location.hostname.split('.')?.slice(-3).join('.')}`;
}

function buildHeaders(requestConfig) {
  const tokenValue = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
  };

  if (tokenValue) {
    headers['Authorization'] = `Bearer ${tokenValue}`;
  }

  const isHighbondUser = isSsoBasedOnUrl(window.location.hostname);
  if (isHighbondUser) {
    headers['isHighbondUser'] = isHighbondUser;
  }

  if (
    typeof window !== 'undefined' &&
    window.location.pathname.includes('/company/compensation/pay-4-performance') &&
    requestConfig?.url &&
    requestConfig.url.includes('CompanyP4PRoute')
  ) {
    headers['X-Request-Source'] = 'compensation-p4p';
  }

  return headers;
}

async function callFetch(method, url, data, requestConfig, skipAuthRefresh) {
  const baseUrl = createBaseUrl();

  let fullUrl = `${baseUrl}${url}`;
  if (requestConfig?.params) {
    const usp = new URLSearchParams();
    for (const [k, v] of Object.entries(requestConfig.params)) {
      if (v == null) continue;
      if (Array.isArray(v)) {
        for (const item of v) usp.append(k, item);
      } else {
        usp.append(k, v);
      }
    }
    const qs = usp.toString();
    if (qs) fullUrl += `?${qs}`;
  }

  const signal = requestConfig?.cancelToken instanceof AbortSignal ? requestConfig.cancelToken : undefined;

  const options = {
    method,
    credentials: 'include',
    headers: buildHeaders({ ...requestConfig, url }),
    signal,
  };

  if (data !== undefined && data !== null) {
    options.body = JSON.stringify(data);
  }

  const response = await fetch(fullUrl, options);
  const tokenExpired =
    (response.status === 401 || response.status === 403) && response.headers.get('token-expired') === 'true';

  if (tokenExpired && !skipAuthRefresh && !requestConfig?.__retryAfterRefresh) {
    const ok = await tryRefreshSession();
    if (ok) {
      return callFetch(method, url, data, { ...requestConfig, __retryAfterRefresh: true }, skipAuthRefresh);
    }
  }

  const missingCookie = response.status === 401 && response.headers.get('missing-authentication-cookie') === 'true';

  if (missingCookie && !requestConfig?.skipAuthErrorHandling) {
    const cached = window.localStorage.getItem('userInfo');
    if (cached) {
      let userid;
      let sessionid;
      try {
        const decode = JSON.parse(cached);
        userid = decode?.User_Id;
        sessionid = decode?.Session_Id;
      } catch {
        // ignore malformed cache
      }
      const accessToken = window.localStorage.getItem('token');

      clearTokenCache();
      await fetch(config.logout, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid, sessionid, accessToken }),
      });

      localStorage.removeItem('token');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('notificationData');
      localStorage.removeItem('alertNotificationData');
      localStorage.removeItem('investorFilterData');
      localStorage.removeItem('companyFilterData');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('preferredCurrency');
      localStorage.removeItem('persist:root');

      const logoutUrl = `https://accounts.${window.location.origin.split('.')?.slice(-2).join('.')}/logout`;
      window.location.href = logoutUrl;
      return;
    }
  }

  let responseData;
  const contentType = response.headers.get('content-type') || '';
  try {
    if (requestConfig?.responseType === 'blob') {
      responseData = await response.blob();
    } else if (requestConfig?.responseType === 'arraybuffer') {
      responseData = await response.arrayBuffer();
    } else if (contentType.includes('application/json') || contentType.includes('+json')) {
      responseData = await response.json();
    } else {
      responseData = (await response.text()) || null;
    }
  } catch {
    responseData = null;
  }

  if (!response.ok) {
    const err = new Error(`Request failed with status ${response.status}`);
    err.response = { status: response.status, headers: response.headers, data: responseData };
    err.config = { ...requestConfig, url };
    throw err;
  }

  return { data: responseData, status: response.status, headers: response.headers };
}

export async function tryRefreshSession() {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    if (!tryAcquireCrossTabRefreshLock()) {
      const waited = await waitForTokenRefreshFromOtherTab();
      if (waited) {
        return true;
      }
      if (!tryAcquireCrossTabRefreshLock()) {
        return false;
      }
    }

    try {
      const r = await callFetch('POST', '/api/v1/auth/refresh', {}, { skipAuthRefresh: true }, true);
      const newToken = r.data?.token;
      if (newToken) {
        localStorage.setItem('token', newToken);
        scheduleProactiveRefresh(newToken);
      }
      return !!newToken;
    } catch (err) {
      if (err.response?.status === 401) {
        performSessionExpiredLogout();
      }
      return false;
    } finally {
      releaseCrossTabRefreshLock();
    }
  })();

  return refreshInFlight.finally(() => {
    refreshInFlight = null;
  });
}

const api = {
  get: (url, requestConfig) => callFetch('GET', url, undefined, requestConfig),
  post: (url, data, requestConfig) => callFetch('POST', url, data, requestConfig),
  put: (url, data, requestConfig) => callFetch('PUT', url, data, requestConfig),
  delete: (url, requestConfig) => callFetch('DELETE', url, undefined, requestConfig),
  request: ({ method = 'GET', url, data, ...rest }) => callFetch(method, url, data, rest),
};

export default api;
